import { get } from 'lodash';
import { UnknownAction } from 'redux';

// Utils
import { getParentScrollableSectionForElement } from '../../../utils/dom/getParentScrollableSection';
import { ScrollCleanupConfig } from './scrollExcessHandler';
import { getScrollTopToCentreSelectionOnSheetClose } from './scrollToElementConfigUtils';

// Selectors
import { getSelectedElementIds } from '../../../element/selection/selectedElementsSelector';
import {
    getCurrentlyEditingEditorKey,
    getCurrentlyEditingId,
} from '../../../element/selection/currentlyEditingSelector';
import { getElementIdForSelectedLabel } from '../../../element/labels/labelSelectors';

// Constants
import {
    ELEMENT_EDIT_START,
    ELEMENT_EDIT_COMPLETE,
    ELEMENTS_DESELECT_ALL,
    ELEMENTS_DESELECTED,
} from '../../../../common/elements/selectionConstants';
import { TABLE_CELL_EDITOR_KEY, TABLE_GRID_EDITOR_KEY } from '../../../../common/table/tableConstants';
import { DOCUMENT_MODAL_EDITOR_KEY } from '../../../element/document/modal/documentModalConstants';
import { SheetActionTypes } from '../../structural/sheet/sheetActionTypes';
import { SheetId } from '../../structural/sheet/sheetTypes';
import { LABEL_RENAME_FINISH } from '../../../../common/labels/labelConstants';

/**
 * Determines whether cell editing is transitioning back to table grid selection.
 */
const isFinishingEditingTableCell = (state: any, action: UnknownAction): boolean => {
    if (action.type !== ELEMENT_EDIT_START || action.editorKey !== TABLE_GRID_EDITOR_KEY) return false;

    const currentlyEditingId = getCurrentlyEditingId(state);
    const currentlyEditingEditorKey = getCurrentlyEditingEditorKey(state);

    return action.id === currentlyEditingId && currentlyEditingEditorKey === TABLE_CELL_EDITOR_KEY;
};

/**
 * Returns the scroll excess cleanup required after an action is dispatched.
 */
export const getScrollCleanupConfigForAction = (state: any, action: UnknownAction): ScrollCleanupConfig | undefined => {
    if (action.type === SheetActionTypes.SHEET_CLOSE) {
        return {
            sheetId: action.sheetId as SheetId,
            scrollTop: getScrollTopToCentreSelectionOnSheetClose(state, action),
        };
    }

    let elementId;

    const currentlyEditingId = getCurrentlyEditingId(state);
    const currentlyEditingEditorKey = getCurrentlyEditingEditorKey(state);

    if (isFinishingEditingTableCell(state, action)) {
        elementId = currentlyEditingId;
    }

    if (action.type === ELEMENT_EDIT_COMPLETE) {
        if (currentlyEditingEditorKey === DOCUMENT_MODAL_EDITOR_KEY) {
            return { scrollableParent: document.getElementById('document-modal-content-scrollable-container') };
        }

        elementId = currentlyEditingId;
    }

    if (action.type === ELEMENTS_DESELECTED) {
        elementId = get(action.ids, 0);
    }

    if (action.type === ELEMENTS_DESELECT_ALL) {
        elementId = get(getSelectedElementIds(state), 0);
    }

    if (action.type === LABEL_RENAME_FINISH) {
        elementId = getElementIdForSelectedLabel(state);
    }

    return (
        elementId && {
            scrollableParent: getParentScrollableSectionForElement(elementId),
            keyboardWillClose: elementId === currentlyEditingId,
        }
    );
};
